import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowSwapHorizontal } from "iconsax-react";
import { ModalForm, Steps, Button, Prompt, Toast, Skeleton } from "@/shared/ui";
import { formatMoneyKzt, toNumber, extractErrorMessage, type Locale } from "@/shared/utils";
import {
  useGetProjectsQuery,
  useGetProjectQuery,
  useRedistributeBudgetMutation,
  PLANNED_FIELDS,
  PLANNED_LABELS,
} from "@/features/projects";
import type { Project, PlannedField } from "@/features/projects";
import { useGetAvailableExternalIncomesQuery } from "@/features/accounting/income";
import { useGetPoolsQuery } from "../../../api";
import {
  buildCells,
  validateSourceStep,
  validateTargetStep,
  validateIncomeSourcesStep,
  buildRedistributePayload,
  makeEmptyInputs,
  toCents,
} from "../../../utils";
import type { RedistributePayloadInput } from "../../../utils";
import RedistributeProjectList from "./RedistributeProjectList";
import RedistributeAmountsTable from "./RedistributeAmountsTable";
import RedistributeConfirm from "./RedistributeConfirm";

interface Props {
  initialSourceId: number | "external_pool" | null;
  onClose: () => void;
  onSuccess?: () => void;
}

interface PromptState {
  title: string;
  text: string;
  variant?: "success" | "error" | "warning";
}

const KNOWN_ERROR_CODES = [
  "same_project",
  "cannot_redistribute_internal",
  "insufficient_external_pool_budget",
  "sources_smeta_mismatch",
  "negative_delta",
  "invalid_budget_category",
  "redistribution_amounts_mismatch",
  "project_not_found",
  "cannot_redistribute_default_project",
  "insufficient_dotted_pot_budget",
];

export default function BudgetRedistributeWizard({ initialSourceId, onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation("FinancePage");
  const { t: tProjects } = useTranslation("ProjectsPage");
  const locale = (i18n.language as Locale) || "ru";

  const [step, setStep] = useState(1);
  const [sourceId, setSourceId] = useState<number | "external_pool" | null>(initialSourceId);
  const [targetId, setTargetId] = useState<number | null>(null);
  const [sourceInputs, setSourceInputs] = useState<Record<PlannedField, string>>(makeEmptyInputs);
  const [targetInputs, setTargetInputs] = useState<Record<PlannedField, string>>(makeEmptyInputs);
  const [incomeInputs, setIncomeInputs] = useState<Record<number, string>>({});
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  const isPotSource = sourceId === "external_pool";

  const { data: allProjects = [] } = useGetProjectsQuery();
  const { data: pools } = useGetPoolsQuery();
  const { data: availableIncomes = [] } = useGetAvailableExternalIncomesQuery();
  const options = allProjects.filter(function selectable(p) {
    return !p.is_internal && (p.status === "active" || p.status === "planned");
  });

  const numericSourceId = typeof sourceId === "number" ? sourceId : null;
  const { data: sourceDetail, isFetching: isSourceFetching } = useGetProjectQuery(numericSourceId ?? 0, {
    skip: numericSourceId == null,
  });
  const { data: targetDetail, isFetching: isTargetFetching } = useGetProjectQuery(targetId ?? 0, {
    skip: targetId == null,
  });
  const [redistribute, { isLoading: isSubmitting }] = useRedistributeBudgetMutation();

  const sourceCells = buildCells(sourceDetail);
  const sourceVal = validateSourceStep(sourceCells, sourceInputs);

  const availableByIncomeId: Record<number, number> = {};
  availableIncomes.forEach(function fill(inc) {
    availableByIncomeId[inc.id] = toCents(inc.available_amount);
  });
  const incomeVal = validateIncomeSourcesStep(incomeInputs, availableByIncomeId);

  const targetCells = buildCells(targetDetail);
  const sourceTotalCents = isPotSource ? incomeVal.totalCents : sourceVal.totalCents;
  const sourceValid = isPotSource ? incomeVal.valid : sourceVal.valid;
  const targetVal = validateTargetStep(targetInputs, sourceTotalCents);

  const sourceProject = options.find((p) => p.id === sourceId);
  const targetProject = options.find((p) => p.id === targetId);
  const sourceName = isPotSource
    ? t("redistribute.externalPoolTitle")
    : (sourceProject?.name ?? sourceDetail?.name ?? "");
  const targetName = targetProject?.name ?? targetDetail?.name ?? "";

  const stepDefs = [
    { id: "source", label: t("redistribute.stepSource") },
    { id: "recipient", label: t("redistribute.stepRecipient") },
    { id: "amounts", label: t("redistribute.stepAmounts") },
    { id: "recipientAmounts", label: t("redistribute.stepRecipientAmounts") },
    { id: "confirm", label: t("redistribute.stepConfirm") },
  ];
  const activeId = stepDefs[step - 1].id;
  const completedIds = stepDefs.slice(0, step - 1).map((s) => s.id);

  function formatDate(value: string | null): string {
    if (!value) return "";
    const dateLocale = locale === "kk" ? "kk-KZ" : locale === "en" ? "en-US" : "ru-RU";
    return new Date(value).toLocaleDateString(dateLocale);
  }

  function metaFor(p: Project): string {
    const statusLabel = t(`redistribute.status.${p.status}`);
    const datePart =
      p.status === "planned"
        ? t("redistribute.fromDate", { date: formatDate(p.start_date) })
        : t("redistribute.toDate", { date: formatDate(p.end_date) });
    return `${statusLabel} · ${datePart}`;
  }

  function handleSelectSource(id: number | "external_pool") {
    setSourceId(id);
    setSourceInputs(makeEmptyInputs());
    setTargetInputs(makeEmptyInputs());
    setIncomeInputs({});
    if (targetId === id) setTargetId(null);
  }

  function handleSelectTarget(id: number | "external_pool") {
    if (typeof id !== "number") return;
    setTargetId(id);
    setTargetInputs(makeEmptyInputs());
  }

  function handleSourceInput(field: PlannedField, value: string) {
    setSourceInputs((prev) => ({ ...prev, [field]: value }));
  }

  function handleTargetInput(field: PlannedField, value: string) {
    setTargetInputs((prev) => ({ ...prev, [field]: value }));
  }

  function handleIncomeInput(incomeId: number, value: string) {
    setIncomeInputs((prev) => ({ ...prev, [incomeId]: value }));
  }

  const canNext =
    (step === 1 && sourceId != null) ||
    (step === 2 && targetId != null) ||
    (step === 3 && sourceValid) ||
    (step === 4 && targetVal.valid);

  function goNext() {
    if (step < 5 && canNext) setStep(step + 1);
  }

  function goBack() {
    if (step > 1) setStep(step - 1);
  }

  function handleStepClick(id: string) {
    const index = stepDefs.findIndex((s) => s.id === id);
    if (index !== -1 && index < step - 1) setStep(index + 1);
  }

  function mapError(err: unknown): string {
    const data = (err as { data?: { error?: string; category?: string; max_movable?: string } } | undefined)?.data;
    const code = data?.error;
    if (code === "category_below_spent") {
      const labelKey = data?.category ? PLANNED_LABELS[data.category as PlannedField] : undefined;
      const category = labelKey ? tProjects(labelKey) : data?.category;
      const max = data?.max_movable != null ? formatMoneyKzt(toNumber(data.max_movable), locale, true) : undefined;
      if (category && max) return t("redistribute.errors.category_below_spent", { category, max });
    }
    if (code && KNOWN_ERROR_CODES.includes(code)) return t(`redistribute.errors.${code}`);
    return extractErrorMessage(err, "redistribute.errors.default", t);
  }

  async function handleSubmit() {
    if (targetId == null) return;
    const payloadInput: RedistributePayloadInput = isPotSource
      ? {
          source_kind: "external_pool",
          target_project_id: targetId,
          incomeInputs,
          targetInputs,
        }
      : {
          source_kind: "project",
          source_project_id: sourceId as number,
          target_project_id: targetId,
          sourceInputs,
          targetInputs,
        };
    try {
      await redistribute(buildRedistributePayload(payloadInput)).unwrap();
      setPrompt({
        title: t("redistribute.successTitle"),
        text: t("redistribute.successText"),
        variant: "success",
      });
    } catch (err: unknown) {
      setPrompt({ title: t("redistribute.errorTitle"), text: mapError(err), variant: "error" });
    }
  }

  function handlePromptClose() {
    const wasSuccess = prompt?.variant === "success";
    setPrompt(null);
    if (wasSuccess) {
      onSuccess?.();
      onClose();
    }
  }

  function renderAmountsSkeleton() {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: PLANNED_FIELDS.length }).map(function renderRow(_, index) {
          return <Skeleton key={index} height={52} />;
        })}
      </div>
    );
  }

  function renderStepContent() {
    if (step === 1) {
      const sourceItems = [
        {
          id: "external_pool" as const,
          name: t("redistribute.externalPoolTitle"),
          meta: t("redistribute.externalPoolSubtitle"),
          amount: formatMoneyKzt(toNumber(pools?.external?.current_balance ?? "0"), locale, false),
        },
        ...options.map((p) => ({
          id: p.id,
          name: p.name,
          meta: metaFor(p),
          amount: formatMoneyKzt(toNumber(p.budget_summary?.remaining ?? "0"), locale, false),
        })),
      ];
      return (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h4 className="text-display-2xs content-base-primary">{t("redistribute.sourceTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("redistribute.sourceSubtitle")}</p>
          </div>
          <RedistributeProjectList
            items={sourceItems}
            selectedId={sourceId}
            onSelect={handleSelectSource}
            amountCaption={t("redistribute.free")}
            searchPlaceholder={t("redistribute.searchPlaceholder")}
            emptyText={t("redistribute.noProjects")}
          />
        </div>
      );
    }

    if (step === 2) {
      const targetItems = options
        .filter((p) => p.id !== sourceId)
        .map((p) => ({
          id: p.id,
          name: p.name,
          meta: metaFor(p),
          amount: formatMoneyKzt(toNumber(p.budget_summary?.remaining ?? "0"), locale, false),
        }));
      return (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h4 className="text-display-2xs content-base-primary">{t("redistribute.recipientTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("redistribute.recipientSubtitle")}</p>
          </div>
          <RedistributeProjectList
            items={targetItems}
            selectedId={targetId}
            onSelect={handleSelectTarget}
            amountCaption={t("redistribute.free")}
            searchPlaceholder={t("redistribute.searchPlaceholder")}
            emptyText={t("redistribute.noProjects")}
          />
        </div>
      );
    }

    if (step === 3 && isPotSource) {
      const rows = availableIncomes.map((inc) => ({
        field: inc.id,
        label: inc.source_name,
        remaining: formatMoneyKzt(toNumber(inc.available_amount), locale, false),
      }));
      return (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h4 className="text-display-2xs content-base-primary">{t("redistribute.amountsPoolTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("redistribute.amountsPoolSubtitle")}</p>
          </div>
          <Toast color="grey" text={t("redistribute.poolSourceHint")} closable={false} autoClose={false} isFullWidth />
          {availableIncomes.length === 0 ? (
            <p className="text-body-regular-sm content-base-secondary py-6 text-center">
              {t("redistribute.noExternalIncomes")}
            </p>
          ) : (
            <RedistributeAmountsTable
              rows={rows}
              values={incomeInputs}
              onChange={handleIncomeInput}
              errors={incomeVal.errors}
              secondaryHeader={t("redistribute.colAvailable")}
              inputHeader={t("redistribute.colTransfer")}
              t={t}
            />
          )}
          <div className="flex items-center justify-between p-3 radius-md surface-secondary-fill">
            <span className="text-body-regular-sm content-base-secondary">{t("redistribute.totalToTransfer")}</span>
            <span className="text-body-bold-md content-action-info">
              {formatMoneyKzt(sourceTotalCents / 100, locale, false)}
            </span>
          </div>
        </div>
      );
    }

    if (step === 3) {
      const rows = PLANNED_FIELDS.map((field) => ({
        field,
        label: tProjects(PLANNED_LABELS[field]),
        remaining: formatMoneyKzt(toNumber(sourceCells[field].available), locale, false),
        planned: formatMoneyKzt(toNumber(sourceCells[field].planned), locale, false),
      }));
      return (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h4 className="text-display-2xs content-base-primary">{t("redistribute.amountsTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("redistribute.amountsSubtitle")}</p>
          </div>
          <Toast color="grey" text={t("redistribute.amountsHint")} closable={false} autoClose={false} isFullWidth />
          {isSourceFetching ? (
            renderAmountsSkeleton()
          ) : (
            <RedistributeAmountsTable
              rows={rows}
              values={sourceInputs}
              onChange={handleSourceInput}
              errors={sourceVal.errors}
              secondaryHeader={t("redistribute.colAvailablePlan")}
              secondaryTooltip={t("redistribute.tooltipInfo")}
              inputHeader={t("redistribute.colTransfer")}
              t={t}
            />
          )}
          <div className="flex items-center justify-between p-3 radius-md surface-secondary-fill">
            <span className="text-body-regular-sm content-base-secondary">{t("redistribute.totalToTransfer")}</span>
            <span className="text-body-bold-md content-action-info">
              {formatMoneyKzt(sourceTotalCents / 100, locale, false)}
            </span>
          </div>
        </div>
      );
    }

    if (step === 4) {
      const rows = PLANNED_FIELDS.map((field) => ({
        field,
        label: tProjects(PLANNED_LABELS[field]),
        remaining: formatMoneyKzt(toNumber(targetCells[field].available), locale, false),
        planned: formatMoneyKzt(toNumber(targetCells[field].planned), locale, false),
      }));
      return (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <h4 className="text-display-2xs content-base-primary">{t("redistribute.recipientAmountsTitle")}</h4>
            <p className="text-body-regular-sm content-base-secondary">{t("redistribute.recipientAmountsSubtitle")}</p>
          </div>
          {isTargetFetching ? (
            renderAmountsSkeleton()
          ) : (
            <RedistributeAmountsTable
              rows={rows}
              values={targetInputs}
              onChange={handleTargetInput}
              secondaryHeader={t("redistribute.colAvailablePlan")}
              secondaryTooltip={t("redistribute.tooltipInfo")}
              inputHeader={t("redistribute.colAdd")}
              t={t}
            />
          )}
          <div className="flex items-center justify-between p-3 radius-md surface-secondary-fill">
            <span className="text-body-regular-sm content-base-secondary">{t("redistribute.distributed")}</span>
            <span className="text-body-bold-sm content-base-primary">
              {formatMoneyKzt(targetVal.totalCents / 100, locale, false)} /{" "}
              {formatMoneyKzt(sourceTotalCents / 100, locale, false)}
            </span>
          </div>
        </div>
      );
    }

    const target_deltas: Partial<Record<PlannedField, string>> = {};
    const source_deltas: Partial<Record<PlannedField, string>> = {};
    PLANNED_FIELDS.forEach(function collect(field) {
      if (toCents(targetInputs[field]) > 0) target_deltas[field] = targetInputs[field];
      if (!isPotSource && toCents(sourceInputs[field]) > 0) source_deltas[field] = sourceInputs[field];
    });
    const total = sourceTotalCents / 100;
    const deltaRows = PLANNED_FIELDS.filter((f) => source_deltas[f] || target_deltas[f]).map((f) => ({
      key: f,
      label: tProjects(PLANNED_LABELS[f]),
      outText: source_deltas[f] ? `- ${formatMoneyKzt(toNumber(source_deltas[f]), locale, false)}` : undefined,
      inText: target_deltas[f] ? `+ ${formatMoneyKzt(toNumber(target_deltas[f]), locale, false)}` : undefined,
    }));
    const incomeRows = isPotSource
      ? Object.entries(incomeInputs)
          .filter(([, v]) => toCents(v) > 0)
          .map(([id, v]) => {
            const inc = availableIncomes.find((i) => String(i.id) === id);
            return {
              key: `income-${id}`,
              label: inc?.source_name ?? `${t("redistribute.incomeFallback")} #${id}`,
              outText: `- ${formatMoneyKzt(toNumber(v), locale, false)}`,
            };
          })
      : [];
    const confirmRows = isPotSource ? [...incomeRows, ...deltaRows] : deltaRows;
    const sourceBudgetBefore = toNumber(sourceDetail?.budget_summary?.total_budget ?? "0");
    const sourceRemainingBefore = isPotSource
      ? toNumber(pools?.external?.current_balance ?? "0")
      : toNumber(sourceDetail?.budget_summary?.remaining ?? "0");
    const targetBudgetBefore = toNumber(targetDetail?.budget_summary?.total_budget ?? "0");
    const targetRemainingBefore = toNumber(targetDetail?.budget_summary?.remaining ?? "0");
    const sourceChangeText = isPotSource
      ? ""
      : `${formatMoneyKzt(sourceBudgetBefore, locale, false)} → ${formatMoneyKzt(sourceBudgetBefore - total, locale, false)}`;
    const sourceRemainingChangeText = `${formatMoneyKzt(sourceRemainingBefore, locale, false)} → ${formatMoneyKzt(sourceRemainingBefore - total, locale, false)}`;
    const targetChangeText = `${formatMoneyKzt(targetBudgetBefore, locale, false)} → ${formatMoneyKzt(targetBudgetBefore + total, locale, false)}`;
    const targetRemainingChangeText = `${formatMoneyKzt(targetRemainingBefore, locale, false)} → ${formatMoneyKzt(targetRemainingBefore + total, locale, false)}`;
    return (
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h4 className="text-display-2xs content-base-primary">{t("redistribute.confirmTitle")}</h4>
          <p className="text-body-regular-sm content-base-secondary">{t("redistribute.confirmSubtitle")}</p>
        </div>
        <RedistributeConfirm
          sourceName={sourceName}
          targetName={targetName}
          rows={confirmRows}
          totalText={formatMoneyKzt(total, locale, false)}
          sourceChangeText={sourceChangeText}
          targetChangeText={targetChangeText}
          sourceRemainingChangeText={sourceRemainingChangeText}
          targetRemainingChangeText={targetRemainingChangeText}
          t={t}
        />
      </div>
    );
  }

  const footer = (
    <div className="flex items-center justify-between gap-3">
      {step > 1 ? (
        <Button variant="secondary" size="lg" type="button" onClick={goBack} disabled={isSubmitting}>
          {t("redistribute.back")}
        </Button>
      ) : (
        <span />
      )}
      {step < 5 ? (
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={goNext}
          disabled={!canNext}
          textDuringDisabled={!canNext ? t("redistribute.rules.stepIncomplete") : undefined}>
          {t("redistribute.next")}
        </Button>
      ) : (
        <Button
          variant="primary"
          size="lg"
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !sourceValid || !targetVal.valid}
          textDuringDisabled={!sourceValid || !targetVal.valid ? t("redistribute.rules.checkAmounts") : undefined}>
          {isSubmitting ? t("redistribute.submitting") : t("redistribute.submit")}
        </Button>
      )}
    </div>
  );

  return (
    <>
      <ModalForm
        icon={ArrowSwapHorizontal}
        width="w-4xl"
        onClose={onClose}
        footer={footer}>
        <div className="flex flex-col gap-6">
          <Steps orientation="horizontal" steps={stepDefs} activeId={activeId} completedIds={completedIds} onStepClick={handleStepClick} />
          {renderStepContent()}
        </div>
      </ModalForm>

      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant || "success"}
          onClose={handlePromptClose}
          namespace="FinancePage"
        />
      )}
    </>
  );
}
